import React, { useState } from 'react'
import { GhostPresentationSection } from './GhostPresentationSection'
import {
  AboutSection,
  AvailabilitySection,
  ContactItem,
  ProfileSection,
} from '../../../profile/components'
import { Profile, useGetBusinessCardScan } from '@hooks'
import { days, Text } from '@components'
import { Image, Modal, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { useLanguage } from '@context'

export const About = ({ profile }: { profile?: Profile }) => {
  const { t } = useLanguage()
  const theme = useTheme()
  const styles = getStyles(theme)
  const { data: cardScan } = useGetBusinessCardScan(profile?.id)
  const [isCardModalVisible, setIsCardModalVisible] = useState(false)

  // Transformer les données pour les contacts
  const contactItems: ContactItem[] = [
    {
      type: 'mail' as const,
      label: t('ghostProfile.email'),
      value: profile?.contacts?.email || '',
      iconName: 'message' as const,
    },
    {
      type: 'website' as const,
      label: t('ghostProfile.website'),
      value: profile?.contacts?.website || '',
      iconName: 'link2' as const,
    },
    {
      type: 'phone' as const,
      label: t('ghostProfile.phone'),
      value: profile?.contacts?.phone || '',
      iconName: 'phone' as const,
    },
  ]

  const aboutCategories = [
    {
      title: t('ghostProfile.hashtags'),
      content: '',
      tags: profile?.pinsHashtags?.map((h) => h.name) || [],
    },
    {
      title: t('ghostProfile.objectives'),
      content: '',
      tags: profile?.goals || [],
    },
    {
      title: t('ghostProfile.hobby'),
      content: '',
      tags: profile?.hobbies || [],
    },
    {
      title: t('ghostProfile.skills'),
      content: '',
      tags: profile?.skills || [],
    },
  ]

  return (
    <>
      {cardScan?.imageUrl && (
        <View style={styles.cardScanSection}>
          <Text type="bodyBold" style={styles.cardScanTitle}>
            {t('importContacts.businessCard')}
          </Text>
          <TouchableOpacity onPress={() => setIsCardModalVisible(true)}>
            <Image
              source={{ uri: cardScan.imageUrl }}
              style={styles.cardImage}
              resizeMode="cover"
            />
          </TouchableOpacity>
          <Modal
            visible={isCardModalVisible}
            transparent
            animationType="fade"
            onRequestClose={() => setIsCardModalVisible(false)}
          >
            <TouchableOpacity
              style={styles.modalOverlay}
              activeOpacity={1}
              onPress={() => setIsCardModalVisible(false)}
            >
              <Image
                source={{ uri: cardScan.imageUrl }}
                style={styles.modalImage}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </Modal>
        </View>
      )}

      <GhostPresentationSection
        birthday={profile?.birthDate}
        languages={profile?.languages}
        nationalities={profile?.nationalities}
        origins={profile?.origins}
      />

      <ProfileSection
        title={t('ghostProfile.livedPlaces')}
        sectionType="places"
        items={
          profile?.residenceHistory?.map((residence) => ({
            id: residence.code,
            name: residence.name,
          })) || []
        }
        noEdit
      />

      <AboutSection title={t('ghostProfile.about')} noEdit categories={aboutCategories} />

      <AvailabilitySection
        title={t('ghostProfile.availabilities')}
        noEdit
        disponibilities={
          profile?.availabilities?.slots?.map((slot) => ({
            dayStart: days.find((day) => day.id === slot.days[0].toString())!,
            dayEnd: days.find((day) => day.id === slot.days[slot.days.length - 1].toString())!,
            timeStart: slot.from,
            timeEnd: slot.to,
          })) || []
        }
        bestMoments={profile?.availabilities?.moments || []}
      />

      <ProfileSection
        title={t('ghostProfile.contactInfo')}
        noEdit
        sectionType="contacts"
        items={contactItems}
      />
    </>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    cardScanSection: {
      paddingHorizontal: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
    },
    cardScanTitle: {
      marginBottom: theme.spacing.sm,
    },
    cardImage: {
      width: '100%',
      height: 200,
      borderRadius: theme.spacing.md,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      justifyContent: 'center',
      alignItems: 'center',
    },
    modalImage: {
      width: '90%',
      height: '80%',
    },
  })
